import {useEffect} from 'react';

import Button from 'components/Button/Button';
import {Formik, Form, ErrorMessage} from 'formik';
import {useGlobalState} from 'hooks/useGlobalState';
import {useSetGlobalState} from 'hooks/useSetGlobalState';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useDispatch} from 'react-redux';
import Select, {StylesConfig, components, MenuListProps, GroupBase} from 'react-select';
import {setModalAC} from 'store/modal/modalSlice';
import {getPubsACS} from 'store/pubs/requestAC';
import {assignPubACS} from 'store/singlePageEditor/requestAC';
import Pub from 'typescript/models/Pub';
import PubPage from 'typescript/models/PubPage';
import * as yup from 'yup';

import './AssignPub.scss';

type PubOption = {
  id: Pub['id'];
  name: Pub['name'];
};

type FormValues = {
  selectedPub: PubOption | null;
};

const selectStyles: StylesConfig<PubOption> = {
  container: (styles) => ({
    ...styles,
    maxWidth: '425px',
    width: '100%',
    color: '#828282',
  }),
  control: (styles) => ({
    ...styles,
    background: 'linear-gradient(45deg, #D6D6D6 1.04%, #E8E8E8 100%)',
    border: 'none',
  }),
  valueContainer: (styles) => ({
    ...styles,
    padding: '13px 15px',
  }),
  dropdownIndicator: (styles) => ({
    ...styles,
    padding: '0px 17px 0px 8px',
    color: 'inherit',
  }),
  clearIndicator: (styles) => ({
    ...styles,
    color: 'inherit',
  }),
  placeholder: (styles) => ({
    ...styles,
    color: 'inherti',
  }),
  input: (styles) => ({
    ...styles,
    padding: 0,
    margin: 0,
  }),
  option: (styles, {isSelected}) => ({
    ...styles,
    background: isSelected ? 'linear-gradient(45deg, #FFBF17 1.04%, #F2E2B8 100%);' : '#e0e0e0',
    '&:hover': {
      backgroundColor: '#bdbdbd',
    },
  }),
};

const pubsPerPage = 8;

interface CustomMenuListProps extends MenuListProps<PubOption, boolean, GroupBase<PubOption>> {
  pubs: PubPage[];
  totalCount: number;
}
const CustomMenuList = ({children, pubs, totalCount, ...restProps}: CustomMenuListProps) => {
  const dispatch = useSetGlobalState();

  return (
    <components.MenuList {...restProps} innerProps={{id: 'scrollable-target'}}>
      <InfiniteScroll
        dataLength={pubs.length}
        hasMore={pubs.length < totalCount}
        loader={null}
        scrollableTarget="scrollable-target"
        next={() => {
          dispatch(getPubsACS({page: 1, perPage: pubs.length + pubsPerPage}));
        }}
      >
        {children}
      </InfiniteScroll>
    </components.MenuList>
  );
};

const assignPubValidationSchema = yup.object({
  selectedPub: yup
    .object({
      id: yup.string(),
      name: yup.string(),
    })
    .nullable()
    .required('You must select pub to proceed'),
});

const AssignPub = () => {
  const dispatch = useDispatch();

  const {list: pubs, totalCount} = useGlobalState((state) => state.pubs.pubs);
  const singleEditor = useGlobalState((state) => state.singlePageEditor.singleEditor);

  const handleClose = () => {
    dispatch(setModalAC(null));
  };

  useEffect(() => {
    dispatch(getPubsACS({page: 1, perPage: pubsPerPage}));
  }, [dispatch]);

  const handleAssignPub = ({selectedPub}: FormValues) => {
    if (!selectedPub || !singleEditor) return;

    dispatch(assignPubACS({pubId: selectedPub.id, userId: +singleEditor.id}));
    handleClose();
  };

  if (!singleEditor) return null;

  const unassignedPubs = pubs.filter((pub) =>
    singleEditor.attributes.pubs.every((assignedPub) => assignedPub.id.toString() !== pub.id),
  );

  return (
    <Formik initialValues={{selectedPub: null}} validationSchema={assignPubValidationSchema} onSubmit={handleAssignPub}>
      {({values, setFieldValue}) => (
        <Form className="assign-pub">
          <h1 className="assign-pub__title">Add pub to the managing list</h1>
          <div className="assign-pub__select-wrapper">
            <Select
              isClearable
              getOptionLabel={(option: PubOption) => option.name}
              getOptionValue={(option: PubOption) => option.id}
              styles={selectStyles}
              components={{
                IndicatorSeparator: () => null,
                // eslint-disable-next-line react/no-unstable-nested-components
                MenuList: (props) => <CustomMenuList {...props} pubs={pubs} totalCount={totalCount} />,
              }}
              placeholder="Select pub from the list"
              options={unassignedPubs.map((pub) => ({
                name: pub.attributes.name,
                id: pub.id,
              }))}
              value={values.selectedPub}
              onChange={(value) => {
                setFieldValue('selectedPub', value);
              }}
            />
            <ErrorMessage component="span" className="assign-pub__select-error" name="selectedPub" />
          </div>
          <div className="assign-pub__actions">
            <Button variant="tertiary-outlined" className="assign-pub__cancel" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" variant="tertiary" className="assign-pub__assign">
              Add to List
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default AssignPub;
